export const encodeURL = (url: string) => url;

export const encodeText = (text: string) => text;

export const encodeEmail = (email: string, subject?: string, body?: string) => {
  return `MATMSG:TO:${email};SUB:${subject ?? ''};BODY:${body ?? ''};;`;
};

export const encodePhone = (phone: string) => `tel:${phone}`;

export const encodeSMS = (phone: string, body?: string) => {
  if (!body) return `sms:${phone}`;
  try {
    const fn = (body as unknown as { toWellFormed?: () => string }).toWellFormed;
    const wellFormed = typeof fn === 'function' ? fn.call(body) : body;
    return `sms:${phone}?body=${encodeURIComponent(wellFormed)}`;
  } catch {
    const sanitized = body.replace(/[\uD800-\uDFFF]/g, '');
    return `sms:${phone}?body=${encodeURIComponent(sanitized)}`;
  }
};

export const encodeWifi = (ssid: string, security: 'WPA' | 'WEP' | 'nopass', password?: string, hidden = false) => {
  const sEscaped = ssid.replace(/([\\;:])/g, '\\$1');
  const pEscaped = password ? password.replace(/([\\;:])/g, '\\$1') : '';
  const hiddenPart = hidden ? 'H:true;' : '';
  return `WIFI:T:${security};S:${sEscaped};P:${pEscaped};${hiddenPart};`;
};

export interface VCardData {
  fn: string;
  phone?: string;
  email?: string;
  org?: string;
  title?: string;
  street?: string;
  address?: string;
  city?: string;
  country?: string;
  url?: string;
  note?: string;
}

export const encodeVCard = (data: VCardData) => {
  const parts = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${data.fn}`,
  ];
  if (data.phone) parts.push(`TEL:${data.phone}`);
  if (data.email) parts.push(`EMAIL:${data.email}`);
  if (data.org) parts.push(`ORG:${data.org}`);
  if (data.title) parts.push(`TITLE:${data.title}`);
  const street = data.street ?? data.address ?? '';
  if (street || data.city || data.country) {
    parts.push(`ADR:;;${street};${data.city ?? ''};;;${data.country ?? ''}`);
  }
  if (data.url) parts.push(`URL:${data.url}`);
  if (data.note) parts.push(`NOTE:${data.note}`);
  parts.push('END:VCARD');
  return parts.join('\n');
};

export const encodeCalendar = (title: string, start: string, end?: string, location?: string, description?: string) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const dtstart = formatDate(start);
  let dtend = end ? formatDate(end) : '';
  if (!dtend && start) {
    const d = new Date(start);
    if (!isNaN(d.getTime())) {
      d.setHours(d.getHours() + 1);
      dtend = formatDate(d.toISOString());
    }
  }

  const parts = [
    'BEGIN:VEVENT',
    `SUMMARY:${title}`,
    `DTSTART:${dtstart}`,
    `DTEND:${dtend}`,
  ];
  if (location) parts.push(`LOCATION:${location}`);
  if (description) parts.push(`DESCRIPTION:${description}`);
  parts.push('END:VEVENT');
  return parts.join('\n');
};

export const encodeGeo = (lat: number, lng: number) => `geo:${lat},${lng}`;
